import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { IconComponent } from "@components/icon/icon.component";
import { flatMap } from "lodash";
import { Subject, firstValueFrom, from, timer } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { AlertValidationScopes, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { Anskaffningsformer, Planeringsstatus } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { FeatureService } from "~app/common/services/feature.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SendViol3ArbetsorderCommand } from "~app/features/administration/systemadministration/tabs/sysadm-viol3/commands/SendViol3ArbetsorderCommand";
import { IArbetsorderVirkesorder, IArbetsorderVirkesorderRule } from "~app/features/virkesorder/virkesorder.models";
import { ArbetsorderService, GetArbetsorderAlertsQueryResult } from "../../../planera/services/arbetsorder.service";
import { ArbetsorderEventStore } from "../../services/arbetsorderEventStore.service";
import { IArbetsorderValidationInfo } from "./IArbetsorderValidationInfo";

@Component({
    selector: "rask-arbetsorder-viol3-button",
    templateUrl: "./arbetsorder-viol3-button.component.html",
    styleUrls: ["./arbetsorder-viol3-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        BusySpinnerComponent,
        IconComponent
    ]
})
export class ArbetsorderViol3ButtonComponent implements OnInit, OnDestroy, OnChanges {
  @Input() arbetsorder: IArbetsorderValidationInfo;
  buttonConfig: IArbetsorderVirkesorder;
  buttonText = "Skicka till VIOL";
  hasValidationMessages = false;
  isDirty: boolean;
  isSendInProgress = false;
  @Input() overriddenValidationRules: IArbetsorderVirkesorderRule[];
  senastSkickad: string;
  eudrKravsDefaultFeatureActive: any;

  private readonly arbetsorderEventStore = inject(ArbetsorderEventStore);
  private readonly arbetsorderService = inject(ArbetsorderService);
  private readonly busyService = inject(BusyService);
  private readonly commandService = inject(CommandService);
  private readonly featureService = inject(FeatureService);
  private readonly formDirtySubject = new Subject<void>();
  private readonly unsubscribe$ = new Subject<void>();

  private internalIsFormDirty: boolean;

  // alla metoder man kan göra override på måste tyvärr defineras här då man inte kan göra .name på en arrow function
  // eslint-disable-next-line sonarjs/public-static-readonly
  static isAtgarderIsTillgangligaMethodname = "isAtgarderIsTillgangliga";

  // eslint-disable-next-line sonarjs/public-static-readonly
  static isSchemalagdMethodname = "isSchemalagd";

  @Input() set isFormDirty(dirty: boolean) {
      this.formDirtySubject.next();

      if (dirty) {
          this.internalIsFormDirty = true;
          return;
      }

      timer(1000).pipe(takeUntil(this.formDirtySubject)).subscribe(() => {
          this.internalIsFormDirty = dirty;
      });
  }

  get showButtonSpinner(): boolean {
      return this.isSendInProgress;
  }

  ngOnChanges(changes: SimpleChanges) {
      if (changes.arbetsorder && changes.arbetsorder.currentValue !== undefined) {
          this.arbetsorderEventStore.getAlerts(this.arbetsorder.id);
          this.buttonConfig = this.getButtonConfig();
      }
  }
  areKoordinaterVerifierade = () => this.arbetsorder.koordinaterVerifierade;
  canSend(): boolean {
      return this.buttonConfig.activeRules.every(x => x.validationFunction());
  }
  isArbetsorderNotDirty = () => !this.internalIsFormDirty;

  isAtgarderIsTillgangliga = () => this.atgarderIsTillgangliga();

  isNotSending = () => !this.isSendInProgress;

  isEudrValid = () => this.arbetsorder.eudrValid;

  isProduktsatt = () => this.arbetsorder.produktsatt;

  isSchemalagd = () => (this.arbetsorder.isExternArbetsorder && this.arbetsorder.viol3) ||
        this.arbetsorderService.isSchemalagd(this.arbetsorder.periodsattPeriod, this.arbetsorder.operationer);

  isVoContractDirty = () => this.isDirty;

  ngOnDestroy() {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
  }

  ngOnInit(): void {
      firstValueFrom(this.featureService.getFeatureActive("EudrKravsDefault")).then(x => {
          this.eudrKravsDefaultFeatureActive = x;
      });

      this.buttonConfig = this.getButtonConfig();
      this.arbetsorderEventStore.arbetsorderStoreEventsLoaded$
          .pipe(takeUntil(this.unsubscribe$))
          .pipe(filter(x => x.has(this.arbetsorder.id)), map(z => z.get(this.arbetsorder.id)))
          .subscribe(y => {
              this.isDirty = y.arbetsorderIsDirty;
              this.senastSkickad = DateUtils.formatDateString(y.senastSkickat, DateUtils.datetimeformatWithoutSeconds);
              this.isSendInProgress = y.arbetsorderIsSending;
              this.buttonConfig = this.getButtonConfig();
              this.handleAlertChange(y.alertInfo);
          });

      this.arbetsorderEventStore.arbetsorderSynchFinished$
          .pipe(takeUntil(this.unsubscribe$))
          .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
          .subscribe(x => {
              this.isDirty = x.arbetsorderIsDirty;
              this.senastSkickad = DateUtils.formatDateString(x.senastSkickat, DateUtils.datetimeformatWithoutSeconds);
          });

      this.arbetsorderEventStore.arbetsorderSendStatusChanged$
          .pipe(takeUntil(this.unsubscribe$))
          .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
          .subscribe(x => {
              this.isSendInProgress = x.arbetsorderIsSending;
          });

      this.arbetsorderEventStore.alertFetchedCompleted$
          .pipe(takeUntil(this.unsubscribe$))
          .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
          .subscribe(x => {
              this.handleAlertChange(x.alertInfo);
          });

      this.arbetsorderEventStore.addArbetsorderList([this.arbetsorder.id]);
  }

  noValidationMessages = () => !this.hasValidationMessages;

  sendViol3(): void {
      const cm = new CommandManager();
      cm.updateCommand(
          new SendViol3ArbetsorderCommand(this.arbetsorder.id)
      );
      this.isSendInProgress = true;
      this.busyService.addWork(from(this.commandService.pushCommands(cm))).subscribe();
  }

  setSkickadDatum(skickad: Date) {
      return DateUtils.formatDateString(skickad, DateUtils.datetimeformatWithoutSeconds);
  }

  setValidationIIcon(active: boolean): string {

      return active ? "far fa-check-circle" : "far fa-circle";
  }

  setValidationColor(active: boolean) {

      return active ? "green" : "gray";
  }

  private atgarderIsTillgangliga(): boolean {
      const validStatuses = [
          Planeringsstatus.Tillgangligt,
          Planeringsstatus.Ajourhallet,
          Planeringsstatus.Utfort
      ];
      return this.arbetsorder.atgarder.every(atg => validStatuses.some(x => atg.planeringsstatus === x));
  }

  private getApplicableRule(metodname: string, defaultRule: IArbetsorderVirkesorderRule): IArbetsorderVirkesorderRule {
      return this.overriddenValidationRules?.some(x => x.overrideFunctionName === metodname) ? this.overriddenValidationRules?.find(x => x.overrideFunctionName === metodname) :
          defaultRule;
  }

  private getButtonConfig(): IArbetsorderVirkesorder {
      // Viol3 använder listor för att kunna använda olika regler för olika anskaffningsformer.
      // Genrella regler läggs till nedan där efter spcifika regler för anskaffningsformerna.
      const rules = this.getCommonRules();
      if (this.arbetsorder.anskaffningsform === Anskaffningsformer.Egenskog ||
            Anskaffningsformer.isVirkesKop(this.arbetsorder.anskaffningsform)) {
          rules.push(this.getApplicableRule("areKoordinaterVerifierade", {
              text: "Drivningspunkter är verifierade",
              validationFunction: this.areKoordinaterVerifierade
          })
          );
          rules.push(this.getApplicableRule(ArbetsorderViol3ButtonComponent.isAtgarderIsTillgangligaMethodname,
              {
                  text: "Alla åtgärder är tillgängliga eller högre",
                  validationFunction: this.isAtgarderIsTillgangliga
              })
          );
          rules.push(this.getApplicableRule(ArbetsorderViol3ButtonComponent.isSchemalagdMethodname,
              {
                  text: "Arbetsordern är schemalagd",
                  validationFunction: this.isSchemalagd
              })
          );
      }

      if (this.arbetsorder.isTerminalImport) {
          rules.push(this.getApplicableRule(ArbetsorderViol3ButtonComponent.isSchemalagdMethodname,
              {
                  text: "Arbetsordern är tidsatt",
                  validationFunction: this.isSchemalagd
              })
          );
      }

      if (this.eudrKravsDefaultFeatureActive && (ObjectUtils.hasValue(this.arbetsorder.ejEudr) && !this.arbetsorder.ejEudr)) {
          rules.push(this.getApplicableRule(this.isEudrValid.name,
              {
                  text: "Anmälan enligt EUDR har status tillgänglig",
                  validationFunction: this.isEudrValid
              }));
      }

      return {
          activeRules: rules,
          isViol2: false
      };
  }

  private getCommonRules(): IArbetsorderVirkesorderRule[] {
      return [
          this.getApplicableRule(this.noValidationMessages.name,
              {
                  text: "Inga felmeddelanden finns",
                  validationFunction: this.noValidationMessages
              }),
          this.getApplicableRule(this.isArbetsorderNotDirty.name,
              {
                  text: "Arbetsordern är sparad",
                  validationFunction: this.isArbetsorderNotDirty
              }),
          this.getApplicableRule(this.isVoContractDirty.name,
              {
                  text: "Avtalsobjekt är ändrat",
                  validationFunction: this.isVoContractDirty
              }),
          this.getApplicableRule(this.isNotSending.name,
              {
                  text: "Ingen pågående sändning",
                  validationFunction: this.isNotSending
              }),
          this.getApplicableRule(this.isProduktsatt.name,
              {
                  text: this.arbetsorder.isTerminalImport ? "Produktsatt, åtminstone ett handelssortiment valt" : "Arbetsordern är produktsatt",
                  validationFunction: this.isProduktsatt
              })
      ];
  }

  private handleAlertChange(alertsResult: GetArbetsorderAlertsQueryResult[]) {
      const messages = flatMap(alertsResult, x1 => x1.alerts)
          .map(x1 => ({
              severity: x1.severity,
              message: x1.message,
              anchor: undefined,
              metadata: x1.metadata,
              code: x1.code,
              validationScope: x1.validationScope
          }));

      const errorCodes = [
          MessageSeverity.Error,
          MessageSeverity.Critical
      ];
      this.hasValidationMessages = messages.some(x => errorCodes.some(e =>
          e === x.severity &&
          // eslint-disable-next-line no-bitwise
            (x.validationScope & AlertValidationScopes.skickaTillViol) > 0));
  }
}
